import { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, ChevronLeft, ChevronRight, Play } from "lucide-react";
import { JOBS } from "../data";
import { fetchJobs, mediaUrl } from "../lib/jobsApi";

const STATIC = JOBS.map((j) => ({ title: j.title, cat: j.cat, type: "image", src: j.src }));

export const RecentJobs = () => {
  const [items, setItems] = useState(STATIC);
  const [active, setActive] = useState(null);

  useEffect(() => {
    fetchJobs()
      .then((rows) => {
        const dynamic = rows
          .slice()
          .reverse()
          .map((r) => ({
            title: r.title,
            cat: r.media_type === "video" ? "Video" : "Recent Job",
            type: r.media_type,
            src: mediaUrl(r.id),
          }));
        setItems([...dynamic, ...STATIC]);
      })
      .catch(() => {});
  }, []);

  const close = () => setActive(null);
  const prev = useCallback((e) => { e?.stopPropagation(); setActive((i) => (i + items.length - 1) % items.length); }, [items.length]);
  const next = useCallback((e) => { e?.stopPropagation(); setActive((i) => (i + 1) % items.length); }, [items.length]);

  return (
    <section id="recent-jobs" data-testid="recent-jobs-section" className="py-20 sm:py-28 bg-brand-surface">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <div className="max-w-2xl mb-14">
          <p className="text-brand-olive font-medium tracking-widest uppercase text-sm mb-3">Recent Jobs</p>
          <h2 className="font-display font-extrabold text-brand-brown text-3xl sm:text-4xl lg:text-5xl tracking-tight leading-tight">
            A look at work I&apos;ve done.
          </h2>
          <p className="mt-4 text-brand-muted text-base sm:text-lg leading-relaxed">
            Real projects around Salford — from bespoke joinery and storage to
            secure lock fitting. Tap any item to view it larger.
          </p>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-3 gap-4 sm:gap-6">
          {items.map((job, i) => (
            <motion.button
              key={`${job.type}-${i}`}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.5, delay: (i % 3) * 0.08 }}
              onClick={() => setActive(i)}
              data-testid={`job-thumb-${i}`}
              className="group relative overflow-hidden rounded-2xl ring-1 ring-brand-line text-left aspect-square"
            >
              {job.type === "video" ? (
                <video src={job.src} muted playsInline preload="metadata" className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
              ) : (
                <img src={job.src} alt={job.title} loading="lazy" className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
              )}
              <div className="absolute inset-0 bg-gradient-to-t from-brand-brownDark/85 via-brand-brown/10 to-transparent opacity-90" />
              {job.type === "video" && (
                <span className="absolute top-3 right-3 w-10 h-10 rounded-full bg-brand-bg/85 backdrop-blur flex items-center justify-center text-brand-brown">
                  <Play size={18} className="fill-current ml-0.5" />
                </span>
              )}
              <div className="absolute bottom-0 left-0 p-4 sm:p-5">
                <span className="inline-block text-[10px] sm:text-xs font-semibold tracking-widest uppercase text-brand-olive bg-brand-bg/15 backdrop-blur rounded-full px-2.5 py-1 mb-2">
                  {job.cat}
                </span>
                <p className="text-[#FCFAF6] font-display font-bold text-sm sm:text-lg leading-tight">{job.title}</p>
              </div>
            </motion.button>
          ))}
        </div>
      </div>

      <AnimatePresence>
        {active !== null && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            onClick={close} data-testid="job-lightbox"
            className="fixed inset-0 z-[60] bg-brand-brownDark/90 backdrop-blur-sm flex items-center justify-center p-4"
          >
            <button onClick={close} data-testid="lightbox-close" className="absolute top-5 right-5 text-[#FCFAF6]/80 hover:text-[#FCFAF6]"><X size={30} /></button>
            <button onClick={prev} data-testid="lightbox-prev" className="absolute left-3 sm:left-8 text-[#FCFAF6]/80 hover:text-[#FCFAF6] p-2"><ChevronLeft size={36} /></button>
            <button onClick={next} data-testid="lightbox-next" className="absolute right-3 sm:right-8 text-[#FCFAF6]/80 hover:text-[#FCFAF6] p-2"><ChevronRight size={36} /></button>
            <motion.div
              key={active}
              initial={{ scale: 0.95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} transition={{ duration: 0.25 }}
              onClick={(e) => e.stopPropagation()} className="max-w-3xl w-full"
            >
              {items[active].type === "video" ? (
                <video src={items[active].src} controls autoPlay playsInline className="w-full max-h-[78vh] object-contain rounded-xl bg-black" />
              ) : (
                <img src={items[active].src} alt={items[active].title} className="w-full max-h-[78vh] object-contain rounded-xl" />
              )}
              <div className="mt-3 text-center">
                <p className="text-brand-olive text-xs font-semibold tracking-widest uppercase">{items[active].cat}</p>
                <p className="text-[#FCFAF6] font-display font-bold text-lg">{items[active].title}</p>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};
